/* eslint-disable react-hooks/exhaustive-deps */
// Modules
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, CheckBox, TextField } from '@dasa-health/components-react'
import { Flex, Spacing } from '@dasa-health/alma-react'
import { SystemBoldViewon, SystemBoldViewoff } from '@dasa-health/icons'

// Components
import { ButtonCIP, ButtonOTP, CustomForm, FieldWithButton, ForgotPasswordButton } from './styles'

// Application
import { useCPFLoginForm } from 'redesign/hooks/useCPFLoginForm'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { useCampaigns } from 'redesign/hooks/useCampaigns'
import { getQueryParamsValue } from 'utils'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'redesign/hooks/useSnackbar'
import { ACCESS_TYPE, useCurrentLaboratory } from 'redesign/hooks/useCurrentLaboratory'
import { WarningServiceIsDown } from '../WarningServiceIsDown'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useRecoverHook } from 'hooks'
import { StayConnectedModalButton } from 'pages/SignUp/PersonalData/components/StayConnectedModalButton'
import { ROUTES_PATH } from 'constants/routesPath'
import { ModalCip } from '../ModalCip'
import { ModalForgotPassword } from '../ModalForgotPassword'

type CPFPhasedLoginFormABTestProps = {
    isServiceDown?: boolean
}

export const CPFPhasedLoginForm = ({ isServiceDown = false }: CPFPhasedLoginFormABTestProps) => {
    const [showModalCip, setShowModalCip] = useState(false)
    const [showModalForgotPassword, setShowModalForgotPassword] = useState(false)
    const [isCheckDisable, setIsCheckDisable] = useState(false)
    const { dispatchAnalytics, dispatchAnalyticsABTest } = usePhasedLogin()
    const { currentLaboratory, layoutType } = useCurrentLaboratory()
    const {
        values,
        errors,
        touched,
        viewPassword,
        isLogin,
        isSubmitting,
        hasValidatedContact,
        failedPasswordCount,
        handleBlur,
        handleSubmit,
        handleChange,
        handleChangeCpf,
        handleViewPassword,
        redirectToForgotPassword,
        resetForm,
        getGeolocation,
        getIP,
    } = useCPFLoginForm()
    const recover = useRecoverHook()
    const { changeCampaingSessionStorage } = useCampaigns()
    const location = useLocation()
    const { openSnackbar, onCloseSnackbar } = useSnackbar()
    const { isFeatureEnabled } = useRemoteConfig()
    const history = useHistory()
    const [isLoginOtp, setIsLoginOtp] = useState(false)

    const FF_AUTH_ENABLE_LOGIN_OTP = isFeatureEnabled('AUTH_ENABLE_LOGIN_OTP')
    const KEEP_LOGGED_IS_ENABLED = isFeatureEnabled('AUTH_KEEP_LOGGED')

    const LOGIN_OTP_IS_ENABLED = useMemo(() => FF_AUTH_ENABLE_LOGIN_OTP && hasValidatedContact, [
        FF_AUTH_ENABLE_LOGIN_OTP,
        hasValidatedContact,
    ])

    const cpfValidation = touched.cpf && errors.cpf
    const passwordValidation = touched.password && errors.password
    const showPasswordField = LOGIN_OTP_IS_ENABLED && values.showPasswordField
    const showKeepLoggedCheckbox = KEEP_LOGGED_IS_ENABLED && values.showPasswordField
    const showButtonCIP =
        !values.showPasswordField && (layoutType === ACCESS_TYPE.agendamento_sem_carrinho_CIP || layoutType === ACCESS_TYPE.resultado)
    const disableCTAButton = !values.cpf || !!errors.cpf || isSubmitting
    const ctaTextButton = isLogin && !isLoginOtp ? 'Acessar' : 'Continuar cadastro'
    const canRequestGeolocation = KEEP_LOGGED_IS_ENABLED && showPasswordField

    const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = e
        handleChangeCpf(e)
        sessionStorage.setItem('cpf', target.value)
    }

    const handleOpenOtpModal = () => {
        recover.setUsername(values.cpf)
        setShowModalForgotPassword(true)
    }

    const redirectToOTP = useCallback(() => {
        recover.setUsername(values.cpf)
        history.push(ROUTES_PATH.LOGIN_WITH_OTP, values.cpf)
    }, [values.cpf])

    const handleForgotPassword = () => {
        LOGIN_OTP_IS_ENABLED ? handleOpenOtpModal() : redirectToForgotPassword(values.cpf)
    }

    useEffect(() => {
        changeCampaingSessionStorage()
    }, [changeCampaingSessionStorage])

    useEffect(() => {
        dispatchAnalyticsABTest('default', currentLaboratory?.name)
        const step = values.showPasswordField ? 'insert_password' : 'insert_cpf'
        dispatchAnalytics(step)
    }, [dispatchAnalytics, dispatchAnalyticsABTest, values.showPasswordField, currentLaboratory])

    useEffect(() => {
        if (showModalCip) {
            setShowModalCip(true)
            const step = values.showPasswordField ? 'insert_password' : 'warning_mode'
            dispatchAnalytics(step)
        }
    }, [dispatchAnalytics, showModalCip, values.showPasswordField])

    useEffect(() => {
        const origin = getQueryParamsValue(location, 'origin')
        if (origin === 'sessao-expirada') {
            openSnackbar({
                variant: 'error',
                message: 'Sessão expirada. Insira seus dados novamente para acessar sua conta Nav Dasa.',
            })
        }
    }, [])

    useEffect(() => {
        LOGIN_OTP_IS_ENABLED &&
            failedPasswordCount >= 2 &&
            setTimeout(() => {
                recover.setUsername(values.cpf)
                setShowModalForgotPassword(true)
            }, 1000)
    }, [LOGIN_OTP_IS_ENABLED, failedPasswordCount])

    useEffect(() => {
        values.cpf.length < 14 && resetForm()
        values.cpf.length === 14 && isLogin && handleSubmit()
        setIsLoginOtp(false)
    }, [values.cpf])

    useEffect(() => {
        if (canRequestGeolocation) {
            getGeolocation(setIsCheckDisable, openSnackbar, onCloseSnackbar)
            getIP()
        }
    }, [showPasswordField])

    useEffect(() => {
        if (canRequestGeolocation) {
            const hasGeocation = !!(values.latitude && values.longitude)
            if (values.keepLogged && !hasGeocation) {
                getGeolocation(setIsCheckDisable, openSnackbar, onCloseSnackbar)
            }
        }
    }, [values.keepLogged, showPasswordField])

    return (
        <>
            <ModalCip setShowModal={setShowModalCip} showModal={showModalCip} />
            <ModalForgotPassword setShowModal={setShowModalForgotPassword} showModal={showModalForgotPassword} />

            <CustomForm onSubmit={handleSubmit}>
                <Flex flexDirection="column" height="100%">
                    {isServiceDown && <WarningServiceIsDown />}
                    <Spacing height={isServiceDown ? 24 : 16} />
                    <TextField
                        autoComplete="username"
                        className="textInput"
                        helperText={touched.cpf ? errors.cpf : ''}
                        inputMode="numeric"
                        label="Digite o CPF para continuar"
                        name="cpf"
                        onBlur={handleBlur}
                        onChange={handleCpfChange}
                        onPaste={() => resetForm()}
                        readOnly={isSubmitting}
                        type="tel"
                        value={values.cpf}
                        variant={cpfValidation ? 'error' : 'default'}
                    />
                    <Spacing height={cpfValidation ? 56 : 24} />
                    {values.showPasswordField && isLogin && (
                        <>
                            <TextField
                                className="textInput"
                                helperText={touched.password ? errors.password : ''}
                                label="Senha"
                                name="password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                readOnly={isSubmitting}
                                trailingIcon={
                                    viewPassword ? (
                                        <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewPassword} />
                                    ) : (
                                        <SystemBoldViewon data-testid="change-type-input" onClick={handleViewPassword} />
                                    )
                                }
                                type={viewPassword ? 'text' : 'password'}
                                value={values.password}
                                variant={passwordValidation ? 'error' : 'default'}
                            />
                            <Spacing height={8} />
                            <ForgotPasswordButton
                                data-current-service="Login faseado"
                                data-item-position="Login CPF"
                                data-item-text="Esqueci a senha"
                                disabled={!values.cpf || isSubmitting}
                                onClick={handleForgotPassword}
                                passwordValidation={!!passwordValidation}
                                type="button"
                            >
                                Esqueci a senha
                            </ForgotPasswordButton>
                            <Spacing height={32} />
                        </>
                    )}
                    <Flex flexDirection="column" marginTop={'initial'}>
                        <Button disabled={disableCTAButton} isLoading={isSubmitting} width="100%">
                            {ctaTextButton}
                        </Button>

                        {showPasswordField && (
                            <ButtonOTP
                                data-current-service="Login faseado"
                                data-item-position="Login CPF"
                                data-item-text="Entrar sem senha"
                                onClick={redirectToOTP}
                                variant="secondary"
                                width="100%"
                            >
                                Entrar sem senha
                            </ButtonOTP>
                        )}

                        {showKeepLoggedCheckbox && (
                            <FieldWithButton>
                                <CheckBox
                                    checked={values.keepLogged}
                                    className={'form__checkbox--error'}
                                    disabled={isCheckDisable}
                                    label={'Permanecer conectado'}
                                    name={'keepLogged'}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    style={{ marginRight: '8px' }}
                                />
                                <StayConnectedModalButton />
                            </FieldWithButton>
                        )}

                        {showButtonCIP && (
                            <ButtonCIP
                                data-current-service="Login faseado"
                                data-item-position="Insira o CPF"
                                data-item-text="Acessar com protocolo"
                                onClick={() => setShowModalCip(true)}
                                size="compact"
                                type="button"
                                variant="tertiary"
                                width="100%"
                            >
                                Acessar com protocolo (CIP)
                            </ButtonCIP>
                        )}
                    </Flex>
                </Flex>
            </CustomForm>
        </>
    )
}
